import * as React from 'react';
import { useParams } from "react-router-dom";
import { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import axios from 'axios';
import { appConstants } from "../_constants/app.constants";
import './TournamentBracket.css';
import BracketGame from './BracketGame';
import BracketTree, { winningPathLength } from './BracketTree';
import TwoSidedBracketTree from './TwoSidedBracketTree';
import { useBracketLayout } from './bracketLayout';
import { transformFlatBracketMatchesToRoots } from './bracketTransform';
import {
  bracketSvgSize,
  buildOneSidedColumns,
  buildTwoSidedColumns,
  collectAllGames,
  shouldRenderTwoSided,
} from './bracketRounds';
import { BracketTopBar, BracketFieldStrip } from './BracketChrome';
import { saveTournamentShields } from '../_utils/shieldUtils';
import { useTournamentPageView } from "../analytics/useTournamentPageView";
import {
  IconCenter,
  IconChevronLeft,
  IconChevronRight,
  IconZoomIn,
  IconZoomOut,
} from "../ui";
import {
  collectImagesFromBracketFlat,
  dedupeTeamImages,
  readCachedTournamentImages,
  resolveTournamentIdForViewer,
} from "../_utils/tournamentViewUtils";

const BREAKPOINTS = { sm: 600, md: 900 };
const SPACING = 8;
const COLOR_TOKENS = {
  divider: "rgba(15, 23, 42, 0.12)",
  "background.paper": "#ffffff",
  "text.secondary": "#64748b",
  primary: "#0f766e",
  secondary: "#b45309",
  warning: "#d97706",
};
const SHADOW_TOKENS = {
  1: "0 2px 8px rgba(15, 23, 42, 0.12)",
  2: "0 8px 20px rgba(15, 23, 42, 0.16)",
};

function spaceValue(value) {
  if (typeof value !== "number") return value;
  return `${value * SPACING}px`;
}

function resolveColor(value) {
  if (typeof value !== "string") return value;
  return COLOR_TOKENS[value] ?? value;
}

function resolveShadow(value) {
  if (typeof value === "number") return SHADOW_TOKENS[value] ?? SHADOW_TOKENS[1];
  return value;
}

function normalizeSx(sx) {
  if (!sx || typeof sx !== "object") return undefined;
  const style = {};
  const borderColorValue = resolveColor(sx.borderColor);
  Object.entries(sx).forEach(([key, value]) => {
    if (value && typeof value === "object") return;
    switch (key) {
      case "p":
        style.padding = spaceValue(value);
        break;
      case "px":
        style.paddingLeft = spaceValue(value);
        style.paddingRight = spaceValue(value);
        break;
      case "py":
        style.paddingTop = spaceValue(value);
        style.paddingBottom = spaceValue(value);
        break;
      case "pt":
        style.paddingTop = spaceValue(value);
        break;
      case "pb":
        style.paddingBottom = spaceValue(value);
        break;
      case "m":
        style.margin = spaceValue(value);
        break;
      case "mx":
        style.marginLeft = value === "auto" ? "auto" : spaceValue(value);
        style.marginRight = value === "auto" ? "auto" : spaceValue(value);
        break;
      case "my":
        style.marginTop = spaceValue(value);
        style.marginBottom = spaceValue(value);
        break;
      case "mt":
        style.marginTop = spaceValue(value);
        break;
      case "mb":
        style.marginBottom = spaceValue(value);
        break;
      case "gap":
      case "rowGap":
      case "columnGap":
        style[key] = spaceValue(value);
        break;
      case "bgcolor":
        style.backgroundColor = resolveColor(value);
        break;
      case "backgroundColor":
        style.backgroundColor = resolveColor(value);
        break;
      case "color":
      case "borderColor":
        style[key] = resolveColor(value);
        break;
      case "borderBottom":
        style.borderBottom =
          typeof value === "number"
            ? `${value}px solid ${borderColorValue ?? COLOR_TOKENS.divider}`
            : value;
        break;
      case "boxShadow":
        style.boxShadow = resolveShadow(value);
        break;
      case "borderRadius":
        style.borderRadius = typeof value === "number" ? `${value * 4}px` : value;
        break;
      default:
        style[key] = value;
        break;
    }
  });
  return style;
}

function useTheme() {
  return {
    breakpoints: {
      down: (key) => `(max-width:${BREAKPOINTS[key] - 0.02}px)`,
      between: (a, b) => `(min-width:${BREAKPOINTS[a]}px) and (max-width:${BREAKPOINTS[b] - 0.02}px)`,
    },
  };
}

function useMediaQuery(query) {
  const getMatch = () => (typeof window !== "undefined" ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);
    listener();
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
}

const Box = React.forwardRef(function Box({ sx, style, className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={className} style={{ ...normalizeSx(sx), ...style }} {...rest}>
      {children}
    </div>
  );
});

const Typography = ({ variant, color, className, children, gutterBottom, ...rest }) => {
  const Tag = variant === "h6" ? "h2" : "p";
  const colorClass = color === "text.secondary" ? "text-ink-muted" : "text-ink";
  return (
    <Tag className={[colorClass, gutterBottom ? "mb-2" : "", className].filter(Boolean).join(" ")} {...rest}>
      {children}
    </Tag>
  );
};

const CircularProgress = ({ size = 60 }) => (
  <div
    className="rounded-full border-2 border-arena/20 border-t-gold animate-spin"
    style={{ width: size, height: size }}
    aria-hidden
  />
);

const Tooltip = ({ title, children }) =>
  React.isValidElement(children)
    ? React.cloneElement(children, {
        title: children.props.title ?? title,
        "aria-label": children.props["aria-label"] ?? title,
      })
    : children;

const Chip = ({ label, className, sx, style, size, color, ...rest }) => (
  <span
    className={[
      "inline-flex items-center rounded-full border px-2 py-1 font-bold",
      color === "warning"
        ? "border-amber-500/30 bg-amber-500 text-white"
        : color === "primary"
          ? "border-arena/40 bg-arena text-white"
          : "border-arena/20 bg-white text-ink-soft",
      size === "small" ? "text-[10px]" : "text-[11px]",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
    style={{ ...normalizeSx(sx), ...style }}
    {...rest}
  >
    {label}
  </span>
);

const IconButton = ({ className, sx, style, children, ...rest }) => (
  <button
    type="button"
    className={[
      "inline-flex h-10 w-10 items-center justify-center rounded-full border border-arena/20 bg-white text-ink shadow-md transition-colors hover:bg-arena/5",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
    style={{ ...normalizeSx(sx), ...style }}
    {...rest}
  >
    {children}
  </button>
);

const Fab = ({ className, size, color, sx, style, children, ...rest }) => (
  <button
    type="button"
    className={[
      "inline-flex items-center justify-center rounded-full border shadow-md transition-colors disabled:opacity-50",
      color === "secondary"
        ? "border-gold-dark/40 bg-gold text-white hover:bg-gold-dark"
        : color === "primary"
          ? "border-arena/40 bg-arena text-white hover:bg-arena-dark"
          : "border-arena/20 bg-white text-ink hover:bg-arena/5",
      size === "small" ? "h-10 w-10" : "h-12 w-12",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
    style={{ ...normalizeSx(sx), ...style }}
    {...rest}
  >
    {children}
  </button>
);

const BRACKET_LINE_INFO = { yOffset: -8, separation: 8, homeVisitorSpread: 14 };
const BRACKET_ROUND_GAP = 40;
/** Menos padding en el SVG = más escala útil en pantalla (sobre todo en altura). */
const BRACKET_SVG_PADDING = 14;

/**
 * Calcula la separación entre rondas para que el cuadro aproveche el espacio
 * horizontal disponible: ajusta su relación de aspecto a la del viewport.
 * Cuadros altos y estrechos (pocas rondas, una columna) ganan separación;
 * cuadros anchos (a dos lados) la reducen para no desbordar.
 */
function computeRoundGap({
  numRounds,
  twoSided,
  isMobile,
  gameW,
  gameH,
  bandHeight,
  svgPadding,
  viewportW,
  viewportH,
}) {
  if (!numRounds || numRounds < 1) return BRACKET_ROUND_GAP;
  const verticalRounds = twoSided ? Math.max(numRounds - 1, 1) : numRounds;
  const cols = twoSided ? Math.max(2 * numRounds - 1, 1) : numRounds;
  const naturalHeight = gameH * Math.pow(2, verticalRounds - 1) + bandHeight + svgPadding * 2;
  const availW = Math.max(viewportW - 24, 320);
  const availH = Math.max(viewportH - (isMobile ? 24 : 152), 240);
  const targetWidth = (availW / availH) * naturalHeight * 0.98;
  const rawGap = (targetWidth - svgPadding * 2) / cols - gameW;
  const minGap = 34;
  const maxGap = isMobile ? 96 : Math.round(gameW * 1.5);
  return Math.round(Math.min(maxGap, Math.max(minGap, rawGap)));
}

/** Firma estable de la respuesta plana para evitar re-renders si no hay cambios reales. */
function bracketFlatSignature(flat) {
  if (!Array.isArray(flat) || flat.length === 0) return 'empty';
  const pick = (m, a, b) => (m[a] !== undefined && m[a] !== null ? m[a] : m[b]);
  return flat
    .map((m) =>
      [
        pick(m, 'id', 'Id'),
        pick(m, 'localGoals', 'LocalGoals'),
        pick(m, 'foreignGoals', 'ForeignGoals'),
        pick(m, 'completed', 'Completed'),
        pick(m, 'isActive', 'IsActive'),
        m.localTeam?.name ?? m.LocalTeam?.name ?? '',
        m.foreignTeam?.name ?? m.ForeignTeam?.name ?? '',
        pick(m, 'groupPositionLocalId', 'GroupPositionLocalId'),
        pick(m, 'groupPositionForeignId', 'GroupPositionForeignId'),
      ].join(':')
    )
    .sort()
    .join('|');
}

// Función mejorada para aplicar escudos a equipos del bracket
function patchBracketTeamsWithShields(brackets, images) {
  if (!Array.isArray(brackets) || !brackets.length) {
    return brackets;
  }
  const imageMap = new Map();
  (Array.isArray(images) ? images : []).forEach((img) => {
    if (!img?.id || !img?.imageData) return;
    imageMap.set(String(img.id).toUpperCase(), img.imageData);
  });

  // Función para aplicar el escudo a un equipo
  const patchTeam = (team) => {
    if (!team || !team.id) {
      return team;
    }
    const mappedShield = imageMap.get(String(team.id).toUpperCase());
    if (mappedShield) {
      return { ...team, shield: mappedShield };
    }
    return team;
  };

  // Recorre un juego y aplica escudos a todos los equipos
  const processGame = (game) => {
    if (!game || !game.sides) return game;
    
    // Copia profunda del objeto para no mutar el original
    const newGame = { ...game };
    const newSides = {};
    
    // Procesar cada lado (home/visitor)
    for (const side in game.sides) {
      if (!game.sides[side]) {
        newSides[side] = game.sides[side];
        continue;
      }
      
      // Copia del lado
      const newSide = { ...game.sides[side] };
      
      // Si tiene equipo, aplicar escudo
      if (newSide.team) {
        newSide.team = patchTeam(newSide.team);
      }
      
      // Si tiene seed con sourceGame, procesar recursivamente
      if (newSide.seed && newSide.seed.sourceGame) {
        newSide.seed = { ...newSide.seed };
        newSide.seed.sourceGame = processGame(newSide.seed.sourceGame);
      }
      
      newSides[side] = newSide;
    }
    
    newGame.sides = newSides;
    return newGame;
  };
  
  // Aplicar a todos los brackets
  return brackets.map(processGame);
}

// Función para verificar si un partido está en curso
function checkActiveMatches(brackets) {
  if (!Array.isArray(brackets) || !brackets.length) return brackets;
  
  // Función para marcar un partido como activo si está en curso
  const checkGameStatus = (game) => {
    if (!game) return game;
    
    // Copia del juego para no mutar el original
    const newGame = { ...game };
    
    // Determinar si el partido está en curso
    // Un partido está activo si tiene hora programada, no está completado,
    // y la hora está dentro de un rango de tiempo razonable (por ejemplo, comenzó hace menos de 2 horas)
    if (newGame.scheduled) {
      const matchTime = new Date(newGame.scheduled);
      const currentTime = new Date();
      const timeDiff = (currentTime - matchTime) / (1000 * 60); // Diferencia en minutos
      
      // Partido activo si comenzó hace menos de 120 minutos y tiene puntuación registrada
      const hasScores = newGame.sides && 
        Object.values(newGame.sides).some(side => side && side.score && side.score.score !== undefined);
      
      // Marcar como activo si el partido está en marcha (comenzó hace poco y tiene puntuación)
      newGame.isActive = hasScores && timeDiff >= -5 && timeDiff <= 120 && !newGame.completed;
    }
    
    // Procesar recursivamente los partidos en las semillas
    if (newGame.sides) {
      const newSides = {};
      
      for (const side in newGame.sides) {
        if (!newGame.sides[side]) {
          newSides[side] = newGame.sides[side];
          continue;
        }
        
        const newSide = { ...newGame.sides[side] };
        
        if (newSide.seed && newSide.seed.sourceGame) {
          newSide.seed = { ...newSide.seed };
          newSide.seed.sourceGame = checkGameStatus(newSide.seed.sourceGame);
        }
        
        newSides[side] = newSide;
      }
      
      newGame.sides = newSides;
    }
    
    return newGame;
  };
  
  // Procesar todos los brackets
  return brackets.map(checkGameStatus);
}

// Separar los brackets en finales y resto de partidos
function separateBrackets(brackets) {
  if (!Array.isArray(brackets) || brackets.length === 0) return { mainBracket: [], thirdPlaceMatch: null };

  // Buscar partido de tercer y cuarto puesto (un partido suelto con "puesto" en el nombre)
  const thirdPlaceMatch = brackets.find(bracket => 
    bracket.name && 
    (bracket.name.toLowerCase().includes('puesto') || 
     bracket.name.toLowerCase().includes('3º') || 
     bracket.name.toLowerCase().includes('3º y 4º'))
  );

  // Si encontramos partido de tercer puesto, quitarlo del array principal
  const mainBracket = thirdPlaceMatch 
    ? brackets.filter(bracket => bracket !== thirdPlaceMatch)
    : brackets;

  return { mainBracket, thirdPlaceMatch };
}

// Componente personalizado para renderizar la línea del conector
const BracketConnectorLine = (props) => {
  return (
    <path
      d={props.d}
      stroke="#0f766e"
      strokeWidth={3}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={0.92}
      style={{
        vectorEffect: 'non-scaling-stroke',
      }}
    />
  );
};

export default function ViewBracket() {
  const [brackets, setBracketMatchs] = useState([]);
  const { tournamentId } = useParams();
  const [tId, setTournamentId] = useState(tournamentId ?? null);
  useTournamentPageView(tId, "bracket");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const lastFlatSignatureRef = useRef('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const bracketLayout = useBracketLayout();
  const bracketGameDimensions = useMemo(
    () => ({ width: bracketLayout.gameWidth, height: bracketLayout.gameHeight }),
    [bracketLayout.gameWidth, bracketLayout.gameHeight]
  );
  const containerRef = useRef(null);
  const bracketMeasureRef = useRef(null);
  /** Tamaño natural del bracket (sin scale) y factor para llenar el viewport. */
  const [viewportFit, setViewportFit] = useState({ w: 0, h: 0, scale: 1 });

  const { mainBracket, thirdPlaceMatch } = useMemo(() => separateBrackets(brackets), [brackets]);
  const totalBracketTrees = mainBracket.length;

  /** Cuadro campeón: el que recibe debajo el partido de 3º/4º puesto. */
  const championshipBracket = useMemo(() => {
    if (mainBracket.length === 0) return null;
    return (
      mainBracket.find((b) => b && b.isBracketRoot) ||
      mainBracket.reduce(
        (best, b) => (winningPathLength(b) > winningPathLength(best) ? b : best),
        mainBracket[0]
      )
    );
  }, [mainBracket]);

  /** Altura de la banda de fases dentro del SVG (proporcional a la tarjeta). */
  const bandHeight = useMemo(
    () => Math.round(bracketGameDimensions.height * 0.34),
    [bracketGameDimensions.height]
  );

  const numRoundsMax = useMemo(
    () => mainBracket.reduce((max, b) => Math.max(max, winningPathLength(b)), 0),
    [mainBracket]
  );

  /** Tamaño de ventana para ajustar el cuadro al espacio disponible. */
  const [windowSize, setWindowSize] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1280,
    h: typeof window !== 'undefined' ? window.innerHeight : 720,
  }));
  useEffect(() => {
    const onResize = () => setWindowSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /** Separación entre rondas adaptada para aprovechar el ancho disponible. */
  const roundGap = useMemo(
    () =>
      computeRoundGap({
        numRounds: numRoundsMax,
        twoSided: numRoundsMax >= 5,
        isMobile,
        gameW: bracketGameDimensions.width,
        gameH: bracketGameDimensions.height,
        bandHeight,
        svgPadding: BRACKET_SVG_PADDING,
        viewportW: windowSize.w,
        viewportH: windowSize.h,
      }),
    [numRoundsMax, isMobile, bracketGameDimensions, bandHeight, windowSize]
  );

  /** Próximos partidos para la franja inferior. */
  const upcomingMatches = useMemo(() => {
    const all = brackets.flatMap((b) => collectAllGames(b));
    return all
      .filter((g) => g && g.scheduled && !g.completed)
      .map((g) => ({ when: new Date(g.scheduled), g }))
      .filter((x) => !Number.isNaN(x.when.getTime()))
      .sort((a, b) => a.when - b.when)
      .slice(0, 8)
      .map((x) => ({
        time: x.when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        label: x.g.name || 'Partido',
        field: String(x.g.fieldName || '').trim(),
      }));
  }, [brackets]);

  const bracketTitle = 'Cuadro de eliminatorias';
  const bracketSubtitle = numRoundsMax > 0 ? `${Math.pow(2, numRoundsMax)} equipos` : '';

  /** Pinta un árbol de bracket eligiendo entre una columna o dos lados. */
  const renderBracketTree = (
    tree,
    keyName,
    { withBand = true, className = 'bracket-svg', svgPixelSize = null, thirdPlaceGame = null } = {}
  ) => {
    const dims = bracketGameDimensions;
    const sizeStyle = svgPixelSize
      ? { width: `${svgPixelSize.width}px`, height: `${svgPixelSize.height}px` }
      : { width: '100%', height: 'auto' };
    const common = {
      GameComponent: BracketGame,
      LineComponent: BracketConnectorLine,
      gameDimensions: dims,
      roundSeparatorWidth: roundGap,
      lineInfo: BRACKET_LINE_INFO,
      svgPadding: BRACKET_SVG_PADDING,
      homeOnTop: true,
      thirdPlaceGame,
      topText: (g) =>
        g && g.scheduled
          ? new Date(g.scheduled).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : '',
      bottomText: (g) => (g && g.name) || '',
      className,
      svgProps: {
        style: {
          overflow: 'visible',
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          shapeRendering: 'geometricPrecision',
          vectorEffect: 'non-scaling-stroke',
          ...sizeStyle,
        },
        preserveAspectRatio: 'xMidYMid meet',
      },
    };

    if (withBand && shouldRenderTwoSided(tree)) {
      const { columns } = buildTwoSidedColumns(
        tree,
        dims.width,
        roundGap,
        BRACKET_SVG_PADDING
      );
      return (
        <TwoSidedBracketTree
          key={keyName}
          game={tree}
          headerColumns={columns}
          bandHeight={bandHeight}
          {...common}
        />
      );
    }

    const bandColumns = withBand
      ? buildOneSidedColumns(tree, dims.width, roundGap, BRACKET_SVG_PADDING).columns
      : null;
    return (
      <BracketTree
        key={keyName}
        game={tree}
        headerColumns={bandColumns}
        bandHeight={withBand ? bandHeight : 0}
        {...common}
      />
    );
  };

  useEffect(() => {
    let active = true;
    const resolveTournament = async () => {
      setLoading(true);
      const resolvedId = await resolveTournamentIdForViewer(tournamentId);
      if (!active) return;
      setTournamentId(resolvedId);
      if (!resolvedId) {
        setLoadError('');
        setBracketMatchs([]);
        setLoading(false);
      }
    };

    resolveTournament();
    return () => {
      active = false;
    };
  }, [tournamentId]);

  // Estados para mejoras móviles
  const [selectedBracketIndex, setSelectedBracketIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Función para detectar capacidad de scroll
  const checkScrollCapability = () => {
    const container = containerRef.current;
    if (!container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  };

  // Función para scroll horizontal suave
  const scrollHorizontally = (direction) => {
    const container = containerRef.current;
    if (!container) return;
    
    const scrollAmount = container.clientWidth * 0.8;
    const targetScroll = direction === 'left' 
      ? container.scrollLeft - scrollAmount 
      : container.scrollLeft + scrollAmount;
    
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  };

  // Función para centrar el bracket
  const centerBracket = () => {
    const container = containerRef.current;
    if (!container) return;
    
    const { scrollWidth, clientWidth } = container;
    const centerPosition = (scrollWidth - clientWidth) / 2;
    
    container.scrollTo({
      left: centerPosition,
      behavior: 'smooth'
    });
  };

  // Función para zoom
  const handleZoom = (direction) => {
    setZoomLevel((z) =>
      direction === 'in' ? Math.min(z * 1.25, 1.8) : Math.max(z / 1.25, 0.3)
    );
  };

  // Función para pantalla completa

  // Función para ocultar el indicador de scroll cuando se hace scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollTimeout;
    
    const handleScroll = () => {
      container.classList.add('scrolling');
      setShowScrollHint(false);
      checkScrollCapability();
      
      // Quitar la clase después de un tiempo para que el indicador reaparezca
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        container.classList.remove('scrolling');
      }, 3000);
    };

    container.addEventListener('scroll', handleScroll);
    
    // Verificar capacidad de scroll inicial
    const checkInitialScroll = () => {
      checkScrollCapability();
      // Mostrar hint de scroll si es necesario
      if (container.scrollWidth > container.clientWidth) {
        setTimeout(() => setShowScrollHint(false), 3000);
      }
    };
    
    // Verificar después de que el contenido se haya renderizado
    setTimeout(checkInitialScroll, 500);
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [brackets]);

  const loadBracketData = useCallback(
    async ({ silent = false } = {}) => {
      const targetId = tId;
      if (!targetId) {
        setLoading(false);
        return;
      }

      const rootEl = containerRef.current;
      let sx = 0;
      let sy = 0;
      if (silent && rootEl) {
        sx = rootEl.scrollLeft;
        sy = rootEl.scrollTop;
      }

      if (!silent) setLoading(true);

      try {
        setLoadError('');
        const bracketResponse = await axios.get(
          `${appConstants.API_URL}/api/tournamentmatches/${targetId}/bracketmatchs/component2`
        );
        const raw = bracketResponse.data;

        if (!raw || !raw.length) {
          lastFlatSignatureRef.current = 'empty';
          setBracketMatchs([]);
          return;
        }

        const sig = bracketFlatSignature(raw);
        if (silent && sig === lastFlatSignatureRef.current) {
          setBracketMatchs((prev) => {
            if (!prev.length) return prev;
            return checkActiveMatches(prev);
          });
          return;
        }
        lastFlatSignatureRef.current = sig;

        const bracketRoots = transformFlatBracketMatchesToRoots(raw);
        if (!bracketRoots.length) {
          setBracketMatchs([]);
          return;
        }

        const cachedImages = readCachedTournamentImages(targetId);
        const freshImages = collectImagesFromBracketFlat(raw);
        const images = dedupeTeamImages([...cachedImages, ...freshImages]);
        if (images.length > 0) {
          saveTournamentShields(targetId, images);
        }

        const bracketWithShields = patchBracketTeamsWithShields(bracketRoots, images);
        const processedBrackets = checkActiveMatches(bracketWithShields);
        setBracketMatchs(processedBrackets);

        if (silent && rootEl) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const el = containerRef.current;
              if (el) {
                el.scrollLeft = sx;
                el.scrollTop = sy;
              }
            });
          });
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status === 403 || status === 404) {
          setLoadError('No tienes acceso público a este torneo.');
        } else {
          setLoadError('No se pudo cargar el bracket.');
        }
        setBracketMatchs([]);
      } finally {
        setLoading(false);
      }
    },
    [tId]
  );

  useEffect(() => {
    loadBracketData({ silent: false });
    const interval = setInterval(() => loadBracketData({ silent: true }), 20000);
    return () => clearInterval(interval);
  }, [loadBracketData]);

  const recomputeViewportFit = useCallback(() => {
    if (isMobile) {
      setViewportFit({ w: 0, h: 0, scale: 1 });
      return;
    }
    const el = bracketMeasureRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    if (w < 48 || h < 48) return;

    const padX = 12;
    const padY = 16;
    /** Reserva para la barra superior y la franja inferior. */
    const chromeReserve = 112;
    const vw = window.visualViewport?.width ?? window.innerWidth;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const availW = vw - padX;
    const availH = vh - padY - chromeReserve;

    const trees = totalBracketTrees;
    const maxZoom = trees <= 1 ? 5.1 : trees === 2 ? 3.85 : trees === 3 ? 3.15 : 2.55;
    const minZoom = 0.28;
    const raw = Math.min(availW / w, availH / h, maxZoom);
    let scale = Math.max(minZoom, raw);
    scale = Math.round(scale * 1000) / 1000;

    setViewportFit((prev) => {
      if (
        prev.w === w &&
        prev.h === h &&
        Math.abs(prev.scale - scale) < 0.012
      ) {
        return prev;
      }
      return { w, h, scale };
    });
  }, [isMobile, totalBracketTrees]);

  /** Móvil: al cargar o cambiar de cuadro, ajusta el zoom para ver todo el ancho. */
  useEffect(() => {
    if (!isMobile || loading) return;
    if (mainBracket.length === 0) return;
    const idx = Math.min(selectedBracketIndex, mainBracket.length - 1);
    const tree = mainBracket[idx];
    const el = containerRef.current;
    if (!tree || !el) return;
    const twoSided = shouldRenderTwoSided(tree);
    const hasThirdPlace = tree === championshipBracket && !!thirdPlaceMatch;
    const size = bracketSvgSize(
      tree,
      twoSided,
      bracketGameDimensions.width,
      bracketGameDimensions.height,
      roundGap,
      BRACKET_SVG_PADDING,
      bandHeight,
      hasThirdPlace
    );
    if (!size.width) return;
    const fit = (el.clientWidth - 24) / size.width;
    setZoomLevel(Number.isFinite(fit) ? Math.min(1, Math.max(0.32, fit)) : 1);
  }, [
    isMobile,
    loading,
    selectedBracketIndex,
    mainBracket,
    thirdPlaceMatch,
    roundGap,
    bandHeight,
    bracketGameDimensions,
  ]);

  useLayoutEffect(() => {
    if (loading || isMobile) return;
    recomputeViewportFit();
    const el = bracketMeasureRef.current;
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => recomputeViewportFit())
        : null;
    if (el && ro) ro.observe(el);
    window.addEventListener('resize', recomputeViewportFit);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', recomputeViewportFit);
    const t = window.setTimeout(recomputeViewportFit, 150);
    return () => {
      window.clearTimeout(t);
      ro?.disconnect();
      window.removeEventListener('resize', recomputeViewportFit);
      vv?.removeEventListener('resize', recomputeViewportFit);
    };
  }, [loading, isMobile, recomputeViewportFit, brackets, bracketGameDimensions]);

  if (!loading && !tId) {
    return (
      <Box sx={{ p: 4, maxWidth: 560, mx: 'auto', textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Falta el torneo en la URL
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Abre esta vista desde el menú del torneo; la ruta debe incluir el identificador del torneo. No se elige ningún torneo automáticamente al abrir /viewbracket sin id.
        </Typography>
      </Box>
    );
  }

  if (!loading && loadError) {
    return (
      <Box sx={{ p: 4, maxWidth: 560, mx: 'auto', textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          {loadError}
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} color="success" />
      </Box>
    );
  }

  // Lista de cuadros para navegación (el 3º/4º va embebido bajo la final).
  const allBrackets = mainBracket;

  // ===== Vista móvil unificada (uno o varios cuadros) =====
  if (isMobile) {
    const safeIndex = Math.min(selectedBracketIndex, Math.max(allBrackets.length - 1, 0));
    const mobileTree = allBrackets[safeIndex];
    const mobileTwoSided = !!mobileTree && shouldRenderTwoSided(mobileTree);
    const mobileThirdPlace =
      mobileTree && mobileTree === championshipBracket ? thirdPlaceMatch : null;
    const naturalSize = mobileTree
      ? bracketSvgSize(
          mobileTree,
          mobileTwoSided,
          bracketGameDimensions.width,
          bracketGameDimensions.height,
          roundGap,
          BRACKET_SVG_PADDING,
          bandHeight,
          !!mobileThirdPlace
        )
      : { width: 0, height: 0 };
    const stageW = Math.round(naturalSize.width * zoomLevel);
    const stageH = Math.round(naturalSize.height * zoomLevel);

    return (
      <Box className="bracket-container bracket-container--mobile">
        <BracketTopBar title={bracketTitle} subtitle={bracketSubtitle} />

        {allBrackets.length > 1 && (
          <div className="bracket-mobile-tabs" role="tablist">
            {allBrackets.map((b, i) => (
              <button
                key={`mtab-${i}`}
                type="button"
                role="tab"
                aria-selected={i === safeIndex}
                className={`bracket-mobile-tab${i === safeIndex ? ' is-active' : ''}`}
                onClick={() => setSelectedBracketIndex(i)}
              >
                {`Cuadro ${i + 1}`}
              </button>
            ))}
          </div>
        )}

        <div className="bracket-mobile-canvas" ref={containerRef}>
          {mobileTree ? (
            <div className="bracket-mobile-stage" style={{ width: stageW, height: stageH }}>
              {renderBracketTree(mobileTree, `mobile-${safeIndex}`, {
                withBand: true,
                className: 'bracket-svg',
                svgPixelSize: { width: stageW, height: stageH },
                thirdPlaceGame: mobileThirdPlace,
              })}
            </div>
          ) : (
            <div className="bracket-mobile-empty">No hay datos de bracket disponibles</div>
          )}
        </div>

        {mobileTree && (
          <div className="bracket-mobile-zoom">
            <button
              type="button"
              aria-label="Alejar"
              className="bracket-mobile-zoombtn"
              onClick={() => handleZoom('out')}
              disabled={zoomLevel <= 0.31}
            >
              <IconZoomOut />
            </button>
            <span className="bracket-mobile-zoomlabel">{Math.round(zoomLevel * 100)}%</span>
            <button
              type="button"
              aria-label="Acercar"
              className="bracket-mobile-zoombtn"
              onClick={() => handleZoom('in')}
              disabled={zoomLevel >= 1.79}
            >
              <IconZoomIn />
            </button>
          </div>
        )}
      </Box>
    );
  }

  // Renderizado normal (desktop y móvil con un solo bracket)
  const fitReady =
    !isMobile && viewportFit.w > 0 && viewportFit.h > 0 && viewportFit.scale > 0;
  const showChrome = !isMobile;

  return (
    <Box
      ref={containerRef}
      className="bracket-container"
      sx={{
        width: '100%',
        height: isMobile ? 'auto' : '100dvh',
        minHeight: isMobile ? '100dvh' : '100dvh',
        overflowX: 'auto',
        overflowY: 'auto',
        padding: 0,
        margin: 0,
        WebkitOverflowScrolling: 'touch',
        scrollBehavior: 'smooth',
        touchAction: 'pan-x pan-y',
        position: 'relative',
        ...(!isMobile && {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxSizing: 'border-box',
          minHeight: '100dvh',
        }),
        ...(isMobile && {
          transform: `scale(${zoomLevel})`,
          transformOrigin: 'center top',
          transition: 'transform 0.3s ease'
        })
      }}
    >
      {showChrome && <BracketTopBar title={bracketTitle} subtitle={bracketSubtitle} />}
      {brackets.length > 0 ? (
        <div
          className={[
            'bracket-outer-wrapper',
            'bracket-fit-scale-wrap',
            mainBracket.length > 2 ? 'main-bracket-many' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={
            fitReady
              ? {
                  width: viewportFit.w * viewportFit.scale,
                  height: viewportFit.h * viewportFit.scale,
                  maxWidth: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  flexGrow: isMobile ? undefined : 1,
                }
              : {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  flexGrow: isMobile ? undefined : 1,
                  minHeight: isMobile ? undefined : 'min(70vh, 720px)',
                }
          }
        >
          <div
            ref={bracketMeasureRef}
            style={{
              transform: isMobile ? undefined : `scale(${viewportFit.scale}) translateZ(0)`,
              transformOrigin: 'center center',
              transition: 'transform 0.25s ease-out',
              WebkitFontSmoothing: 'antialiased',
            }}
          >
          <div className="bracket-layout">
            {/* Bracket principal (el 3º/4º puesto va embebido bajo la final) */}
            <div className={`main-bracket${mainBracket.length > 2 ? ' many-trees' : ''}`}>
              {mainBracket.map((bracket, index) => (
                <div key={`bracket-wrapper-${index}`} className="single-bracket-wrapper">
                  {renderBracketTree(bracket, `bracket-${index}`, {
                    thirdPlaceGame: bracket === championshipBracket ? thirdPlaceMatch : null,
                  })}
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', flexGrow: isMobile ? undefined : 1 }}>
          <Typography variant="h6" color="text.secondary">
            No hay datos de bracket disponibles
          </Typography>
        </Box>
      )}

      {showChrome && <BracketFieldStrip matches={upcomingMatches} />}

      {/* Controles de navegación horizontal para móvil */}
      {isMobile && (
        <>
          {/* Botones de scroll horizontal */}
          {canScrollLeft && (
            <IconButton
              sx={{
                position: 'fixed',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: 2,
                zIndex: 1000,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
              }}
              onClick={() => scrollHorizontally('left')}
            >
              <IconChevronLeft />
            </IconButton>
          )}
          
          {canScrollRight && (
            <IconButton
              sx={{
                position: 'fixed',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: 2,
                zIndex: 1000,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
              }}
              onClick={() => scrollHorizontally('right')}
            >
              <IconChevronRight />
            </IconButton>
          )}

          {/* Controles flotantes */}
          <Box sx={{ 
            position: 'fixed', 
            bottom: 16, 
            right: 16, 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1,
            zIndex: 1000
          }}>
            <Tooltip title="Zoom In">
              <Fab 
                size="small" 
                color="primary" 
                onClick={() => handleZoom('in')}
                disabled={zoomLevel >= 2}
              >
                <IconZoomIn />
              </Fab>
            </Tooltip>
            <Tooltip title="Zoom Out">
              <Fab 
                size="small" 
                color="primary" 
                onClick={() => handleZoom('out')}
                disabled={zoomLevel <= 0.5}
              >
                <IconZoomOut />
              </Fab>
            </Tooltip>
            <Tooltip title="Centrar bracket">
              <Fab 
                size="small" 
                color="secondary" 
                onClick={centerBracket}
              >
                <IconCenter />
              </Fab>
            </Tooltip>
          </Box>

          {/* Indicador de scroll horizontal */}
          {showScrollHint && (canScrollLeft || canScrollRight) && (
            <Box sx={{
              position: 'fixed',
              bottom: 100,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 2,
              fontSize: '0.8rem',
              zIndex: 1000,
              animation: 'fadeInOut 3s ease-in-out'
            }}>
              Desliza horizontalmente para ver mas
            </Box>
          )}

          {/* Indicador de zoom */}
          {zoomLevel !== 1 && (
            <Chip 
              label={`${Math.round(zoomLevel * 100)}%`}
              size="small"
              sx={{ 
                position: 'fixed', 
                bottom: 16, 
                left: 16,
                zIndex: 1000
              }}
            />
          )}
        </>
      )}
    </Box>
  );
}
